import React, { useId, useState } from 'react';
import { ROLES, STAGE_STATUSES, sentenceMapErrors, tokensForStage } from './model.js';
import './sentenceMap.css';
import SentenceRoleLegend from './SentenceRoleLegend.jsx';

function Blocks({ tokens }) {
    return tokens.map(t => <span key={t.id} className="btl-sentence-token" data-role={t.role}>
        <span className="btl-sentence-role">{ROLES[t.role]}{t.status === 'error' ? ' · ERROR' : ''}</span>
        <span>{t.text}</span>
    </span>);
}
function Stage({ stage, map }) {
    const tokens = tokensForStage(map, stage.id), groups = [];
    for (const token of tokens) {
        const last = groups[groups.length - 1];
        if (last && token.clauseId && last.clauseId === token.clauseId) last.tokens.push(token);
        else groups.push({ clauseId: token.clauseId, label: token.clauseLabel, tokens: [token] });
    }
    return <section className="btl-sentence-stage" data-stage-id={stage.id} data-status={stage.status || 'unclassified'} aria-label={stage.label}>
        {stage.status && <span className="btl-sentence-stage-status">{STAGE_STATUSES[stage.status]}</span>}
        <h4>{stage.label}</h4>
        <p className="btl-sentence-complete">{stage.sentence}</p>
        <div className="btl-sentence-blocks">
            {groups.map((g, i) => g.clauseId
                ? <details key={g.clauseId} className="btl-sentence-clause">
                    <summary>{g.label} — show role blocks</summary>
                    <div className="btl-sentence-blocks" aria-hidden="true"><Blocks tokens={g.tokens} /></div>
                </details>
                : <span key={i} className="btl-sentence-blocks" aria-hidden="true"><Blocks tokens={g.tokens} /></span>)}
        </div>
        {(stage.note || tokens.some(t => t.note)) && <details className="btl-sentence-notes">
            <summary>Hints and explanation</summary>
            {stage.note && <p>{stage.note}</p>}
            <ul>{tokens.filter(t => t.note).map(t => <li key={t.id}>{t.text}: {t.note}</li>)}</ul>
        </details>}
    </section>;
}
function MapView({ map }) {
    const titleId = useId();
    const [step, setStep] = useState(0), [all, setAll] = useState(false), [revealed, setRevealed] = useState(false);
    const exercise = map.mode === 'exercise';
    // Do not mount coloured answers or fallback sentences before explicit reveal.
    const canShow = !exercise || revealed;
    const shown = all ? map.stages : [map.stages[Math.min(step, map.stages.length - 1)]];
    const shownIds = new Set(shown.map(s => s.id));
    const relations = map.relations.filter(r => {
        const from = map.tokens.find(t => t.id === r.from), to = map.tokens.find(t => t.id === r.to);
        return shownIds.has(from.stageId) && shownIds.has(to.stageId);
    });
    return <figure className="btl-sentence-map" aria-labelledby={titleId}>
        <figcaption id={titleId}>{map.title}</figcaption>
        {exercise && <div className="btl-sentence-controls">
            <p>Try the task above, then compare with the worked example.</p>
            <button type="button" aria-expanded={revealed} onClick={() => setRevealed(!revealed)}>{revealed ? 'Hide worked example' : 'Show worked example'}</button>
        </div>}
        {canShow && <>
            <SentenceRoleLegend />
            <div className="btl-sentence-controls" aria-label="Diagram steps">
                <button type="button" disabled={!all && step === 0} onClick={() => { setStep(Math.max(0, step - 1)); setAll(false); }}>Previous step</button>
                <span role="status">{all ? 'All stages' : 'Stage ' + (step + 1) + ' of ' + map.stages.length}</span>
                <button type="button" disabled={!all && step === map.stages.length - 1} onClick={() => { setStep(Math.min(map.stages.length - 1, step + 1)); setAll(false); }}>Next step</button>
                <button type="button" aria-pressed={all} onClick={() => setAll(!all)}>{all ? 'Show one stage' : 'Show all stages'}</button>
            </div>
            <div className="btl-sentence-stages" data-layout={all ? map.layout : 'stacked'}>
                {shown.map(s => <Stage key={s.id} stage={s} map={map} />)}
            </div>
            {relations.length > 0 && <aside className="btl-sentence-relations" aria-label="Compare sentence choices">
                {relations.map(r => {
                    const from = map.tokens.find(t => t.id === r.from), to = map.tokens.find(t => t.id === r.to);
                    const label = t => map.stages.find(s => s.id === t.stageId).label;
                    return <p key={r.id} data-relation={r.kind}>
                        <span>{label(from)}: <strong>{from.text}</strong></span>
                        <span aria-hidden="true"> {r.kind === 'movement' ? '→' : '↔'} </span>
                        <span className="sr-only"> compared with </span>
                        <span>{label(to)}: <strong>{to.text}</strong></span>
                        <span className="btl-sentence-relation-label">{r.label}</span>
                    </p>;
                })}
            </aside>}
        </>}
    </figure>;
}
export default function SentenceMap({ value }) {
    if (value === undefined) return null;
    if (sentenceMapErrors(value).length) return <p className="text-book-muted text-sm">Diagram unavailable. Please use the lesson text.</p>;
    // A different draft/lesson never inherits a revealed answer or old stage.
    return <MapView key={JSON.stringify(value)} map={{ ...value, relations: value.relations || [] }} />;
}
